/**
 * CSV upload validation — validates parsed CSV rows against sample field rules.
 * All dropdown values are imported from statusOptions (single source of truth).
 */
import {
  conditions,
  priorities,
  sampleStatuses,
  sampleTypes,
} from '../../constants/statusOptions';
import { CSV_MAX_ROWS, CSV_MAX_FILE_SIZE_BYTES } from '../../constants/config';
import { CSV_TEMPLATE_HEADERS } from '../../utils/csvParser';

const REQUIRED_HEADERS = ['Sample Reference', 'Depth Top (m)', 'Type'];
/** Canonical key of the optional per-row location column (FEAT-2026-0017). */
export const LOCATION_ID_HEADER = 'Location ID';
/** Location.LocationId is NVARCHAR(50) in the schema. */
const LOCATION_ID_MAX_LENGTH = 50;
/** Legacy aliases — accept old column names */
const HEADER_ALIASES: Record<string, string> = {
  'sample id': 'Sample Reference',
  // FEAT-2026-0017: engineers label the borehole column many ways.
  'location': LOCATION_ID_HEADER,
  'borehole': LOCATION_ID_HEADER,
  'borehole id': LOCATION_ID_HEADER,
  'hole id': LOCATION_ID_HEADER,
  'test pit': LOCATION_ID_HEADER,
  'depth (m)': 'Depth Top (m)',
  // BUG-2026-0170: imperial files may sensibly label their depth columns in
  // feet — accept them; the import dialog's unit question is what actually
  // designates the unit (values are never converted).
  'depth top (ft)': 'Depth Top (m)',
  'depth base (ft)': 'Depth Base (m)',
  'depth (ft)': 'Depth Top (m)',
};

export interface FieldIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface CsvRowValidation {
  row: Record<string, string>;
  rowNumber: number;
  valid: boolean;
  errors: FieldIssue[];
  normalized: NormalizedSampleRow | null;
}

export interface NormalizedSampleRow {
  /**
   * FEAT-2026-0017: the borehole / test-pit id this row belongs to, verbatim
   * from the optional "Location ID" column. Empty string = "the location
   * currently selected in the side panel" (the pre-feature behaviour).
   */
  LocationId: string;
  SampleId: string;
  SampleReference: string;
  DepthTop: number;
  DepthBase?: number;
  SampleType: string;
  Status: string;
  Priority: string;
  Condition: string;
  SampleComments: string;
  BoxOrBucketNumber: string;
  StorageLocation: string;
}

export interface FileValidationError {
  message: string;
}

/** Strips HTML tags from a string */
const stripHtml = (s: string): string => s.replace(/<[^>]*>/g, '');

/** Case-insensitive match against a readonly array, returns canonical form or null */
function matchEnum(value: string, options: readonly string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  return options.find((o) => o.toLowerCase() === lower) ?? null;
}

/** Validate file-level constraints before parsing rows */
export function validateFile(file: File): FileValidationError | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { message: 'Please upload a .csv file.' };
  }
  if (file.size > CSV_MAX_FILE_SIZE_BYTES) {
    return { message: 'File exceeds 2 MB limit.' };
  }
  return null;
}

/** Validate CSV headers — returns error message or null. Applies aliases (e.g. "Sample ID" → "Sample Reference"). */
export function validateHeaders(headers: string[]): FileValidationError | null {
  const normalized = headers.map((h) => h.trim().toLowerCase());
  for (const req of REQUIRED_HEADERS) {
    const directMatch = normalized.includes(req.toLowerCase());
    const aliasMatch = Object.entries(HEADER_ALIASES).some(
      ([alias, target]) => target === req && normalized.includes(alias),
    );
    if (!directMatch && !aliasMatch) {
      return {
        message: `CSV headers do not match the template. Missing required column: "${req}". Expected: ${CSV_TEMPLATE_HEADERS.join(', ')}`,
      };
    }
  }
  return null;
}

/** Remap legacy header aliases in parsed rows (mutates row keys) */
export function applyHeaderAliases(rows: Record<string, string>[]): void {
  for (const row of rows) {
    for (const [alias, target] of Object.entries(HEADER_ALIASES)) {
      for (const key of Object.keys(row)) {
        if (key.toLowerCase() === alias && !(target in row)) {
          row[target] = row[key];
        }
      }
    }
  }
}

/** Validate post-parse constraints (row count, empty file) */
export function validateRowCount(rowCount: number): FileValidationError | null {
  if (rowCount === 0) {
    return { message: 'CSV file contains no data rows. Please add sample data below the header row.' };
  }
  if (rowCount > CSV_MAX_ROWS) {
    return { message: `CSV exceeds maximum of ${CSV_MAX_ROWS} rows. Please split into smaller files.` };
  }
  return null;
}

/** Validate a single row and return normalized data or errors */
export function validateRow(
  row: Record<string, string>,
  rowNumber: number,
): CsvRowValidation {
  const errors: FieldIssue[] = [];

  // Optional: Location ID (FEAT-2026-0017) — blank rows fall back to the
  // selected location at import time; the id itself is not resolved here.
  const locationId = (row[LOCATION_ID_HEADER] ?? '').trim();
  if (locationId.length > LOCATION_ID_MAX_LENGTH) {
    errors.push({
      field: LOCATION_ID_HEADER, severity: 'error',
      message: `Location ID exceeds ${LOCATION_ID_MAX_LENGTH} characters (row ${rowNumber})`,
    });
  }

  // Required: Sample Reference
  const sampleId = (row['Sample Reference'] ?? '').trim();
  if (!sampleId) {
    errors.push({ field: 'Sample Reference', message: `Sample Reference is required (row ${rowNumber})`, severity: 'error' });
  }

  // Required: Depth Top (m) — also accept legacy "Depth (m)"
  const depthStr = (row['Depth Top (m)'] ?? row['Depth (m)'] ?? '').trim();
  let depth = NaN;
  if (!depthStr) {
    errors.push({ field: 'Depth Top (m)', message: `Depth Top is required (row ${rowNumber})`, severity: 'error' });
  } else {
    depth = Number(depthStr);
    if (isNaN(depth) || depth < 0) {
      errors.push({ field: 'Depth Top (m)', message: `Depth Top must be a non-negative number (row ${rowNumber})`, severity: 'error' });
    }
  }

  // Optional: Depth Base (m)
  const depthBaseStr = (row['Depth Base (m)'] ?? '').trim();
  let depthBase: number | undefined;
  if (depthBaseStr) {
    depthBase = Number(depthBaseStr);
    if (isNaN(depthBase) || depthBase < 0) {
      errors.push({ field: 'Depth Base (m)', message: `Depth Base must be a non-negative number (row ${rowNumber})`, severity: 'warning' });
      depthBase = undefined;
    }
  }

  // Required: Type — warn if unrecognized, default to first option
  const typeRaw = (row['Type'] ?? '').trim();
  let sampleType = matchEnum(typeRaw, sampleTypes);
  if (!typeRaw) {
    errors.push({ field: 'Type', message: `Type is required (row ${rowNumber})`, severity: 'error' });
  } else if (!sampleType) {
    sampleType = typeRaw; // Keep the original value as-is
    errors.push({
      field: 'Type', severity: 'warning',
      message: `Unknown Type '${typeRaw}' — not in standard list: ${sampleTypes.join(', ')} (row ${rowNumber})`,
    });
  }

  // Optional: Sample Status — warn if unrecognized, keep default
  const statusRaw = (row['Sample Status'] ?? '').trim();
  let status = 'Received';
  if (statusRaw) {
    const matched = matchEnum(statusRaw, sampleStatuses);
    if (!matched) {
      errors.push({
        field: 'Sample Status', severity: 'warning',
        message: `Unknown Status '${statusRaw}' — defaulting to 'Received' (row ${rowNumber})`,
      });
    } else {
      status = matched;
    }
  }

  // Optional: Priority — warn if unrecognized, keep default
  const priorityRaw = (row['Priority'] ?? '').trim();
  let priority = 'Normal';
  if (priorityRaw) {
    const matched = matchEnum(priorityRaw, priorities);
    if (!matched) {
      errors.push({
        field: 'Priority', severity: 'warning',
        message: `Unknown Priority '${priorityRaw}' — defaulting to 'Normal' (row ${rowNumber})`,
      });
    } else {
      priority = matched;
    }
  }

  // Optional: Condition — warn if unrecognized, leave blank
  const conditionRaw = (row['Condition'] ?? '').trim();
  let condition = '';
  if (conditionRaw) {
    const matched = matchEnum(conditionRaw, conditions);
    if (!matched) {
      errors.push({
        field: 'Condition', severity: 'warning',
        message: `Unknown Condition '${conditionRaw}' — field left blank (row ${rowNumber})`,
      });
    } else {
      condition = matched;
    }
  }

  // Optional: Comments (max 500 chars, strip HTML)
  let comments = stripHtml((row['Comments'] ?? '').trim());
  if (comments.length > 500) {
    errors.push({ field: 'Comments', message: `Comments exceeds 500 characters (row ${rowNumber})`, severity: 'warning' });
    comments = comments.slice(0, 500);
  }

  // Optional: Shipment Box/Bucket (max 100 chars)
  let box = (row['Shipment Box/Bucket'] ?? '').trim();
  if (box.length > 100) {
    errors.push({ field: 'Shipment Box/Bucket', message: `Shipment Box/Bucket exceeds 100 chars (row ${rowNumber})`, severity: 'warning' });
    box = box.slice(0, 100);
  }

  // Optional: Storage Location
  const storageLocation = (row['Storage Location'] ?? '').trim().slice(0, 200);

  // Only hard errors (severity: 'error') prevent import
  const hasHardErrors = errors.some((e) => e.severity === 'error');
  const valid = !hasHardErrors;
  const normalized: NormalizedSampleRow | null = valid ? {
    LocationId: locationId,
    SampleId: sampleId,
    SampleReference: sampleId,
    DepthTop: depth,
    ...(depthBase !== undefined ? { DepthBase: depthBase } : {}),
    SampleType: sampleType || typeRaw,
    Status: status,
    Priority: priority,
    Condition: condition,
    SampleComments: comments,
    BoxOrBucketNumber: box,
    StorageLocation: storageLocation,
  } : null;

  return { row, rowNumber, valid, errors, normalized };
}

/**
 * Check for duplicate Location + Sample ID + Depth within the uploaded file.
 * FEAT-2026-0017: the same sample id at the same depth in two DIFFERENT
 * boreholes is legitimate, so the key is scoped per location (case-insensitive,
 * blank = the selected location — matching the server's per-location check).
 */
export function checkInFileDuplicates(validations: CsvRowValidation[]): void {
  const seen = new Map<string, number>();
  for (const v of validations) {
    if (!v.normalized) continue;
    const key = `${v.normalized.LocationId.toLowerCase()}|${v.normalized.SampleId}|${v.normalized.DepthTop}`;
    const prev = seen.get(key);
    if (prev !== undefined) {
      v.valid = false;
      const where = v.normalized.LocationId ? ` in location '${v.normalized.LocationId}'` : '';
      v.errors.push({
        field: 'Sample Reference', severity: 'error',
        message: `Duplicate sample: '${v.normalized.SampleId}' at depth ${v.normalized.DepthTop}${where} already exists in this file (row ${v.rowNumber}, first seen row ${prev})`,
      });
      v.normalized = null;
    } else {
      seen.set(key, v.rowNumber);
    }
  }
}
